import { Platform } from 'react-native';
import { useEffect } from 'react';
import usePersistedKeyValueStore, { UsePersistedKeyValueStoreArgs } from './usePersistedKeyValueStore';
import { HubspotTypes } from '../primary-record-types';
import useUserUTMParameters from './api/useUserUTMParameters';

type UTMParameters = HubspotTypes.UTMParameters;

const utmParameterKeys: (keyof UTMParameters)[] = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
];

const sharedUsePersistedKeyValueStoreArgs: UsePersistedKeyValueStoreArgs<UTMParameters | null> = {
    category: 'url-params',
    key: 'utmParameters',
    defaultValue: null,
    expiration: 1000 * 60 * 60, // 1 hour
};

export const useCaptureUrlUTMParameters = () => {
    const { setValue } = usePersistedKeyValueStore(sharedUsePersistedKeyValueStoreArgs);
    const isWeb = Platform.OS === 'web';

    useEffect(() => {
        if (!isWeb || !window?.location?.search) return;
        const urlParams = new URLSearchParams(window.location.search);
        const getParamOrUndefined = (param: string) => urlParams.get(param) || undefined;
        const utmParameters = {
            utm_source: getParamOrUndefined('utm_source'),
            utm_medium: getParamOrUndefined('utm_medium'),
            utm_campaign: getParamOrUndefined('utm_campaign'),
            utm_term: getParamOrUndefined('utm_term'),
            utm_content: getParamOrUndefined('utm_content'),
        };

        // Only set the value if at least one of the UTM parameters is present
        const hasUTMParameters = utmParameterKeys.some((key) => !!utmParameters[key]);
        if (hasUTMParameters) {
            setValue(utmParameters);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
};

export const useCapturedUrlUTMParameters = (): UTMParameters | {} => {
    const { value } = usePersistedKeyValueStore(sharedUsePersistedKeyValueStoreArgs);
    return value || {};
};

export const useRecordCapturedUrlUTMParameters = () => {
    const { value } = usePersistedKeyValueStore(sharedUsePersistedKeyValueStoreArgs);
    const { recordUserUTMParameters } = useUserUTMParameters();

    useEffect(() => {
        recordUserUTMParameters(value);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
};
